// src/pages/Settings.js
import React, { useState, useEffect, useCallback, useContext } from 'react';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import { getUserLevel } from '../services/auth';
import { SettingsContext } from '../context/SettingsContext'; // Importe o SettingsContext

const Settings = () => {
    const navigate = useNavigate();
    const userLevel = getUserLevel();
    // Obtém as preferências, a função de atualização e os estados de carregamento/erro do contexto
    const { preferences, updatePreferences, isLoadingPreferences, errorLoadingPreferences } = useContext(SettingsContext);

    // Usa um estado local para os campos do formulário para permitir edição antes de salvar
    const [localPreferences, setLocalPreferences] = useState(preferences);
    const [logoFile, setLogoFile] = useState(null); // Estado para o arquivo de logo selecionado
    const [message, setMessage] = useState('');
    const [error, setError] = useState(''); // Erro específico do salvamento, além do erro de carregamento do contexto
    const [isSaving, setIsSaving] = useState(false); // Para controlar o estado de salvamento

    // Sincroniza o estado local do formulário com as preferências do contexto
    // Isso é importante quando as preferências são carregadas inicialmente ou quando
    // há uma atualização do backend (ex: outra aba salvou ou fetch inicial termina)
    useEffect(() => {
        setLocalPreferences(preferences);
    }, [preferences]);

    // Redireciona se o usuário não for admin ou não estiver logado
    useEffect(() => {
        if (!localStorage.getItem("token") || userLevel !== 'admin') {
            //Alert.alert("Você Não Tem Permissão Para Acessar a Pagina ")
            navigate("/login");
            return;
        }
    }, [navigate, userLevel]);

    // Lida com mudanças nos campos de texto/cor/checkbox
    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setLocalPreferences(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    }, []);

    // Lida com a seleção de um novo arquivo de logo
    const handleFileChange = useCallback((e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file); // Armazena o arquivo para ser enviado depois
            // Opcional: pré-visualização instantânea do novo logo (usando Data URL)
            const reader = new FileReader();
            reader.onloadend = () => {
                setLocalPreferences(prev => ({ ...prev, logoUrl: reader.result })); // Atualiza a URL localmente para pré-visualização
            };
            reader.readAsDataURL(file);
        } else {
            setLogoFile(null); // Limpa o arquivo se nada for selecionado
            // Se o usuário deselecionou o arquivo, reverte a URL do logo para o que estava antes
            setLocalPreferences(prev => ({ ...prev, logoUrl: preferences.logoUrl }));
        }
    }, [preferences.logoUrl]); // Depende da URL do logo do contexto para reverter

    // Lida com o salvamento das configurações
    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setMessage('');
        setError('');

        try {
            // updatePreferences agora gerencia o upload do logo e o salvamento das preferências
            const success = await updatePreferences(localPreferences, logoFile);

            if (success) {
                setMessage('Configurações salvas com sucesso!');
                setLogoFile(null); // Limpa o arquivo após o sucesso do upload/salvamento
            } else {
                // Isso só aconteceria se updatePreferences retornasse false explicitamente,
                // mas normalmente ele lançaria um erro em caso de falha.
                setError('Erro ao salvar configurações. Por favor, tente novamente.');
            }
        } catch (err) {
            let errorMessage = 'Erro ao salvar configurações. Verifique sua conexão ou permissões.';
            if (err.response && err.response.data && err.response.data.message) {
                errorMessage = err.response.data.message; // Mensagem de erro da API
            } else if (err.message) {
                errorMessage = err.message; // Outros erros (ex: Multer errors)
            }
            setError(errorMessage);
            console.error('Erro detalhado ao salvar configurações:', err);
        } finally {
            setIsSaving(false);
        }
    }, [localPreferences, logoFile, updatePreferences]);

    // Exibe um estado de carregamento enquanto as configurações são buscadas do backend
    if (isLoadingPreferences) {
        return (
            <div>
                <Header />
                <div className="container" style={{ maxWidth: '800px', margin: '50px auto', padding: '20px', textAlign: 'center' }}>
                    <p>Carregando configurações do sistema...</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Header />
            <div className="container" style={{ maxWidth: '800px', margin: '50px auto', padding: '20px' }}>
                <h1 style={{ marginBottom: '30px', textAlign: 'center', color: '#34495e' }}>Configurações do Sistema</h1>

                {/* Mensagens de sucesso ou erro do salvamento */}
                {message && (
                    <div style={{ backgroundColor: '#d4edda', color: '#155724', padding: '10px', borderRadius: '4px', marginBottom: '15px', border: '1px solid #c3e6cb' }}>
                        {message}
                    </div>
                )}
                {error || errorLoadingPreferences ? ( // Exibe erro do salvamento ou do carregamento do contexto
                    <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '10px', borderRadius: '4px', marginBottom: '15px', border: '1px solid #f5c6cb' }}>
                        {error || errorLoadingPreferences}
                    </div>
                ) : null}

                <div style={formCardStyle}>
                    <h2 style={cardTitleStyle}>Logo do Sistema</h2>
                    <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <div style={{ width: '100px', height: '100px', border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            {localPreferences.logoUrl && (
                                <img src={localPreferences.logoUrl} alt="Logo Atual" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            )}
                        </div>
                        <div style={{ flex: 1 }}>
                            <label htmlFor="logoFile" style={labelStyle}>Carregar Novo Logo:</label>
                            <input
                                type="file"
                                id="logoFile"
                                name="logoFile"
                                accept="image/jpeg,image/png,image/gif,image/svg+xml"
                                onChange={handleFileChange}
                                style={inputStyle}
                                disabled={isSaving}
                            />
                            <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>Formatos: JPEG, PNG, GIF, SVG. Tamanho máximo recomendado: 2MB. O logo anterior será substituído.</p>
                        </div>
                    </div>
                </div>

                <div style={formCardStyle}>
                    <h2 style={cardTitleStyle}>Exibição Padrão de Valores</h2>
                    <div style={checkboxContainerStyle}>
                        <input
                            type="checkbox"
                            id="defaultShowValues"
                            name="defaultShowValues"
                            checked={localPreferences.defaultShowValues}
                            onChange={handleChange}
                            style={checkboxStyle}
                            disabled={isSaving}
                        />
                        <label htmlFor="defaultShowValues" style={labelStyle}>Mostrar valores por padrão ao carregar a página</label>
                    </div>
                </div>

                <div style={formCardStyle}>
                    <h2 style={cardTitleStyle}>Cor do Cabeçalho</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <label htmlFor="headerColor" style={labelStyle}>Escolher Cor:</label>
                        <input
                            type="color"
                            id="headerColor"
                            name="headerColor"
                            value={localPreferences.headerColor}
                            onChange={handleChange}
                            style={colorInputStyle}
                            disabled={isSaving}
                        />
                        <input
                            type="text"
                            id="headerColorText"
                            name="headerColor"
                            value={localPreferences.headerColor}
                            onChange={handleChange}
                            placeholder="#RRGGBB"
                            style={{ ...inputStyle, width: '120px' }}
                            disabled={isSaving}
                        />
                    </div>
                </div>

                <button
                    onClick={handleSave}
                    style={saveButtonStyle}
                    disabled={isSaving}
                >
                    {isSaving ? 'Salvando...' : 'Salvar Preferências'}
                </button>
            </div>
        </div>
    );
};

// --- ESTILOS PARA A PÁGINA DE CONFIGURAÇÕES (mantidos os mesmos) ---
const formCardStyle = {
    backgroundColor: 'white',
    padding: '25px',
    borderRadius: '8px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
    marginBottom: '25px',
    border: '1px solid #e0e0e0',
};

const cardTitleStyle = {
    fontSize: '1.4rem',
    color: '#34495e',
    marginBottom: '20px',
    borderBottom: '1px solid #f0f0f0',
    paddingBottom: '10px',
    fontWeight: 'bold',
};

const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#555',
    fontSize: '14px',
};

const inputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '15px',
    boxSizing: 'border-box',
};

const checkboxContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
};

const checkboxStyle = {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
};

const colorInputStyle = {
    width: '50px',
    height: '40px',
    border: 'none',
    padding: '0',
    cursor: 'pointer',
    borderRadius: '4px',
    overflow: 'hidden',
};

const saveButtonStyle = {
    padding: '12px 25px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '16px',
    display: 'block',
    width: '100%',
    maxWidth: '200px',
    margin: '30px auto 0 auto',
    transition: 'background-color 0.2s ease',
};

export default Settings;